import { GitBranch, Square } from "lucide-react";
import { UNITS } from "../../lib/slaLadder";
import { FINAL_OUTCOMES, MAX_REMINDERS } from "../../lib/slaQuestion";
import { Select, inputClass } from "./primitives";

// The condition after a question: one white card, one row of three outcomes (Yes, No, No answer)
// divided by hairlines. Every control is 32px, every label sits in the same 64px column, and each
// outcome's destination is pinned to the bottom of its cell, so the three line up across.

const FIELD = `${inputClass} !h-8 !rounded-md !px-2 !text-[13px]`;
const PICK = "[&_select]:!h-8 [&_select]:!rounded-md [&_select]:!pl-2 [&_select]:!pr-6 [&_select]:!text-[13px]";
const DOTS = { yes: "#12B76A", no: "#F04438", silent: "#F79009" };

function Cell({ tone, title, testId, goes, children }) {
  return (
    <div data-testid={testId} className="flex min-w-0 flex-col gap-2 px-3 py-3">
      <div className="flex items-center gap-[6px] text-[13px] font-semibold text-[#101828]">
        <span className="h-[7px] w-[7px] flex-none rounded-full" style={{ background: DOTS[tone] }} aria-hidden="true" />
        {title}
      </div>
      <div className="flex flex-1 flex-col gap-2 text-[13px] text-[#344054]">{children}</div>
      <div className="mt-1 min-h-[26px] truncate border-t border-[#F2F4F7] pt-2 text-[12px] text-[#667085]" data-testid={`${testId}-leads`}>
        {goes}
      </div>
    </div>
  );
}

// A label and its control on one row, labels in a fixed column so the controls start at the same x.
function Row({ label, children }) {
  return (
    <div className="flex min-h-8 items-center gap-[6px]">
      <span className="w-[52px] flex-none text-[12px] text-[#667085]">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-[6px]">{children}</div>
    </div>
  );
}

function UnitSelect({ value, onChange, label, testId }) {
  return (
    <Select className={`w-[66px] flex-none ${PICK}`} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} data-testid={testId}>
      {UNITS.map((u) => (
        <option key={u.value} value={u.value}>
          {u.label}
        </option>
      ))}
    </Select>
  );
}

/**
 * The condition after a question step, drawn between the question and the step that waits on it.
 *
 *   q          the question's settings as the editor holds them (see lib/slaQuestion.js)
 *   onChange   called with the fields that changed
 *   next       the staff step a "no" releases, with its position, or null when none follows
 */
export default function SlaConditionNode({ id, q, onChange, next }) {
  const closing = (q.closingTemplate || "").trim();
  return (
    <div className="jp-node rounded-xl border border-[#D0D5DD] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06)]" data-testid={`condition-${id}`}>
      <div className="flex items-center gap-2 border-b border-[#EAECF0] px-4 py-[10px]">
        <span className="flex h-5 w-5 flex-none rotate-45 items-center justify-center rounded-[5px] border border-[#98A2B3] text-[#475467]" aria-hidden="true">
          <GitBranch size={11} className="-rotate-45" />
        </span>
        <span className="text-[13px] font-semibold text-[#101828]">Has the customer's issue been resolved?</span>
      </div>

      <div className="grid grid-cols-1 divide-y divide-[#EAECF0] xl:grid-cols-3 xl:divide-x xl:divide-y-0">
        <Cell tone="yes" title="Yes" testId={`branch-yes-${id}`} goes="Complaint resolved">
          <span>Stop the ladder</span>
        </Cell>

        <Cell tone="no" title="No" testId={`branch-no-${id}`} goes={next ? `Go to step ${next.position}` : "Go to the end"}>
          {next ? <span>Send step {next.position} ({next.name})</span> : <span>No step follows</span>}
          <Row label="When">
            <Select
              className={`w-[130px] ${PICK}`}
              value={q.noNow ? "now" : "after"}
              onChange={(e) => onChange({ noNow: e.target.value === "now" })}
              aria-label="When to send after a no"
              data-testid={`no-mode-${id}`}
            >
              <option value="now">Immediately</option>
              <option value="after">After a wait</option>
            </Select>
          </Row>
          {!q.noNow && (
            <Row label="Wait">
              <input
                type="number"
                min="0"
                className={`${FIELD} !w-[56px] text-right`}
                value={q.noAmount}
                onChange={(e) => onChange({ noAmount: e.target.value })}
                aria-label="Wait after a no"
                data-testid={`no-amount-${id}`}
              />
              <UnitSelect value={q.noUnit} onChange={(noUnit) => onChange({ noUnit })} label="Wait unit after a no" testId={`no-unit-${id}`} />
            </Row>
          )}
        </Cell>

        <Cell tone="silent" title="No answer" testId={`branch-silent-${id}`} goes={closing ? "Close, then closing message" : "Close the complaint"}>
          <Row label="Wait">
            <input
              type="number"
              min="1"
              className={`${FIELD} !w-[56px] text-right`}
              value={q.waitAmount}
              onChange={(e) => onChange({ waitAmount: e.target.value })}
              aria-label="Wait for an answer"
              data-testid={`wait-amount-${id}`}
            />
            <UnitSelect value={q.waitUnit} onChange={(waitUnit) => onChange({ waitUnit })} label="Wait unit for an answer" testId={`wait-unit-${id}`} />
          </Row>
          <Row label="Remind">
            <input
              type="number"
              min="0"
              max={MAX_REMINDERS}
              className={`${FIELD} !w-[56px] text-right`}
              value={q.reminders}
              onChange={(e) => onChange({ reminders: e.target.value })}
              aria-label="Number of reminders"
              data-testid={`reminders-${id}`}
            />
            <span className="text-[12px] text-[#667085]">{Number(q.reminders) === 1 ? "time" : "times"}</span>
          </Row>
          <Row label="Reminder">
            <input
              className={FIELD}
              value={q.reminderTemplate}
              placeholder="Same as question"
              onChange={(e) => onChange({ reminderTemplate: e.target.value })}
              aria-label="Reminder template"
              data-testid={`reminder-template-${id}`}
            />
          </Row>
          <Row label="Closing">
            <input
              className={FIELD}
              value={q.closingTemplate}
              placeholder="None"
              onChange={(e) => onChange({ closingTemplate: e.target.value })}
              aria-label="Closing template"
              data-testid={`closing-template-${id}`}
            />
          </Row>
        </Cell>
      </div>
    </div>
  );
}

/**
 * The end of a ladder whose last step is a question: what happens when the customer answers "no"
 * and nothing is left to escalate to.
 */
export function SlaEndNode({ id, q, onChange }) {
  return (
    <div className="jp-node rounded-xl border border-[#D0D5DD] bg-white px-4 py-[10px] shadow-[0_1px_2px_rgba(16,24,40,0.06)]" data-testid={`end-${id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#475467] text-white" aria-hidden="true">
          <Square size={8} fill="currentColor" />
        </span>
        <span className="text-[13px] font-semibold text-[#101828]">End of the ladder</span>
        <span className="text-[12px] text-[#667085]">If the last answer is No</span>
        <Select
          className={`ml-auto w-[260px] ${PICK}`}
          value={q.finalOutcome}
          onChange={(e) => onChange({ finalOutcome: e.target.value })}
          aria-label="What the end of the ladder does"
          data-testid={`final-outcome-${id}`}
        >
          {FINAL_OUTCOMES.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}
